import { isNil, xor, toLower, toString, union } from 'lodash';

import {
  IndustryTree,
  getMarkets,
  parseRules,
  // getIndustries,
  // updateIndustryRule,
  updateMarketRule,
  getParentForChildPrimaryMarket,
} from '../../functions';
import type { ParsedRule } from '../../functions';
import { filters } from '../../constants';
import { PRIMARY_MARKETS, SECONDARY_MARKETS } from '@/constants/markets';

export const mapMarkets = (markets: string[]) => {
  const countryEntries = Object.keys(PRIMARY_MARKETS);
  const exchangeEntries = Object.keys(SECONDARY_MARKETS);
  const countries: string[] = [];
  const exchangeSymbols: string[] = [];
  markets.forEach((market) => {
    if (countryEntries.includes(market)) {
      countries.push(market);
    }
    if (exchangeEntries.includes(market)) {
      exchangeSymbols.push(market);
    }
  });
  return {
    countries,
    exchangeSymbols,
  };
};
export const resolveMarkets = (
  marketId: string,
  depth: number,
  countries: string[],
  exchangeSymbols: string[]
) => {
  const selectedCountries =
    depth === 0
      ? xor(countries.map(toLower), [marketId])
      : countries.map(toLower);
  const selectedExchangeSymbols =
    depth === 1
      ? xor(exchangeSymbols.map(toLower), [marketId])
      : exchangeSymbols.map(toLower);

  let updatedExchangeSymbols = selectedExchangeSymbols;
  let updatedCountries = selectedCountries;

  if (depth === 0) {
    if (!countries.map(toLower).includes(marketId)) {
      // Country was not selected before, now add all children as well
      updatedExchangeSymbols = union(
        selectedExchangeSymbols,
        PRIMARY_MARKETS[marketId.toLowerCase()].children
      );
    } else {
      // Country was selected before but removed now, remove the children
      const { children } = PRIMARY_MARKETS[marketId.toLowerCase()];
      if (children) {
        updatedExchangeSymbols = selectedExchangeSymbols.filter(
          (x) => children.indexOf(x) < 0
        );
      }
    }
  }

  if (depth === 1) {
    //Get the parent
    const parentCountryId = getParentForChildPrimaryMarket(marketId);

    if (!isNil(parentCountryId)) {
      if (!selectedExchangeSymbols.includes(marketId)) {
        // Child was removed, remove the parent as well
        updatedCountries = selectedCountries.filter(
          (x) => x !== parentCountryId
        );
      } else {
        //Child was selected, check if all children are selected
        //and toggle parent accordingly
        const { children } = PRIMARY_MARKETS[parentCountryId];

        if (children) {
          const missingChildren = children.filter(
            (x) => selectedExchangeSymbols.indexOf(x) < 0
          );
          if (
            missingChildren.length === 0 &&
            !updatedCountries.includes(parentCountryId)
          ) {
            //No children are missing, add the parent to country list
            updatedCountries.push(parentCountryId);
          }
        }
      }
    }
  }
  return {
    updatedCountries,
    updatedExchangeSymbols,
  };
};

const getUpdatedMarket = (rules: string, marketId: string, depth: number) => {
  const parsedRules = parseRules(rules);
  const markets = getMarkets(parsedRules);
  const { countries, exchangeSymbols } = markets;
  const { updatedCountries, updatedExchangeSymbols } = resolveMarkets(
    marketId,
    depth,
    countries,
    exchangeSymbols
  );
  return {
    updatedCountries,
    updatedExchangeSymbols,
  };
};
export const getUpdatedMarketRules = (
  rules: string,
  marketId: string,
  depth: number
): ParsedRule[] => {
  const { updatedCountries, updatedExchangeSymbols } = getUpdatedMarket(
    rules,
    marketId,
    depth
  );
  const parsedRules = parseRules(rules);
  return updateMarketRule(
    updatedCountries,
    updatedExchangeSymbols,
    parsedRules
  );
};

export const mapIndustries = (industries: string[], industryList) => {
  const primaryKeys = Object.keys(industryList[0]);
  const secondaryKeys = Object.keys(industryList[1]);
  const tertiaryKeys = Object.keys(industryList[2]);
  const collectedPrimary: string[] = [];
  const collectedSecondary: string[] = [];
  const collectedTertiary: string[] = [];

  industries.forEach((industry) => {
    if (primaryKeys.includes(industry)) {
      collectedPrimary.push(industry);
    }
    if (secondaryKeys.includes(industry)) {
      collectedSecondary.push(industry);
    }
    if (tertiaryKeys.includes(industry)) {
      collectedTertiary.push(industry);
    }
  });
  return {
    primary: collectedPrimary,
    secondary: collectedSecondary,
    tertiary: collectedTertiary,
  };
};

export const getUpdatedIndustries = (
  industryId: string,
  depth: number,
  primary: string[],
  secondary: string[],
  tertiary: string[],
  industryTree: IndustryTree,
  industryList
) => {
  const selectedPrimary =
    depth === 0 ? xor(primary.map(toString), [industryId]) : primary;
  const selectedSecondary =
    depth === 1 ? xor(secondary.map(toString), [industryId]) : secondary;
  const selectedTertiary =
    depth === 2 ? xor(tertiary.map(toString), [industryId]) : tertiary;

  let updatedPrimaryIndustries = selectedPrimary;
  let updatedSecondaryIndustries = selectedSecondary;
  let updatedTertiaryIndustries = selectedTertiary;

  if (depth === 0) {
    //Primary was toggled
    if (selectedPrimary.includes(industryId)) {
      //Primary was added
      //Add all Secondary and Tertiary
      updatedSecondaryIndustries = union(
        selectedSecondary,
        industryList[0][industryId].children
      );
      updatedTertiaryIndustries = union(
        selectedTertiary,
        industryList[0][industryId].children.reduce(
          (acc: string[], current: string) => [
            ...acc,
            ...(industryList[1][current].children as string[]),
          ],
          []
        )
      );
    } else {
      //Primary was removed
      //Remove all Secondary and Tertiary
      updatedSecondaryIndustries = selectedSecondary.filter(
        (x) => industryList[0][industryId].children.indexOf(x) < 0
      );
      updatedTertiaryIndustries = selectedTertiary.filter(
        (x) =>
          industryList[0][industryId].children
            .reduce(
              (acc: string[], current: string) => [
                ...acc,
                ...(industryList[1][current].children as string[]),
              ],
              []
            )
            .indexOf(x) < 0
      );
    }
  } else if (depth === 1) {
    const secondaryIndustry =
      industryTree.secondary && industryTree.secondary[industryId];

    //Secondary was toggled
    if (selectedSecondary.includes(industryId)) {
      //Secondary was added
      //Add all Tertiary
      updatedTertiaryIndustries = union(
        selectedTertiary,
        industryList[1][industryId].children
      );
    } else {
      //Secondary was removed
      //Remove all Tertiary
      updatedTertiaryIndustries = selectedTertiary.filter(
        (x) => industryList[1][industryId].children.indexOf(x) < 0
      );
    }

    //Check if all the children of primary are now selected
    if (!isNil(secondaryIndustry) && !isNil(secondaryIndustry.parentId)) {
      const missingChildren = industryList[0][
        secondaryIndustry.parentId
      ].children.filter((x) => updatedSecondaryIndustries.indexOf(x) < 0);

      if (missingChildren.length === 0) {
        //No children are missing, add the primary to list
        updatedPrimaryIndustries = union(updatedPrimaryIndustries, [
          secondaryIndustry.parentId,
        ]);
      } else {
        //Children are missing, remove primary
        updatedPrimaryIndustries = updatedPrimaryIndustries.filter(
          (x) => x !== secondaryIndustry.parentId
        );
      }
    }
  } else if (depth === 2) {
    // Tertiary was toggled
    const tertiaryIndustry =
      industryTree.tertiary && industryTree.tertiary[industryId];

    //Check if all the children of secondary are now selected
    if (!isNil(tertiaryIndustry) && !isNil(tertiaryIndustry.parentId)) {
      const missingChildren = industryList[1][
        tertiaryIndustry.parentId
      ].children.filter((x) => updatedTertiaryIndustries.indexOf(x) < 0);

      if (missingChildren.length === 0) {
        //No children are missing, add the secondary to the list
        updatedSecondaryIndustries = union(updatedSecondaryIndustries, [
          tertiaryIndustry.parentId,
        ]);
      } else {
        //Children are missing, remove secondary
        updatedSecondaryIndustries = updatedSecondaryIndustries.filter(
          (x) => x !== tertiaryIndustry.parentId
        );
      }

      //Now check primary
      const secondaryIndustry =
        industryTree.secondary &&
        industryTree.secondary[tertiaryIndustry.parentId];

      if (!isNil(secondaryIndustry) && !isNil(secondaryIndustry.parentId)) {
        const primaryId = secondaryIndustry.parentId;

        const missingChildren = industryList[0][primaryId].children.filter(
          (x) => updatedSecondaryIndustries.indexOf(x) < 0
        );

        if (missingChildren.length === 0) {
          //No children are missing, add the primary to list
          updatedPrimaryIndustries = union(updatedPrimaryIndustries, [
            primaryId,
          ]);
        } else {
          //Children are missing, remove primary
          updatedPrimaryIndustries = updatedPrimaryIndustries.filter(
            (x) => x !== primaryId
          );
        }
      }
    }
  }
  return {
    updatedPrimaryIndustries,
    updatedSecondaryIndustries,
    updatedTertiaryIndustries,
  };
};

// export const getUpdatedIndustriesRules = (
//   rules: string,
//   industryTree: IndustryTree,
//   industryId: string,
//   depth: number
// ) => {
//   const parsedRule = parseRules(rules);
//   const { primary, secondary, tertiary } = getIndustries(parsedRule);
//   const {
//     updatedPrimaryIndustries,
//     updatedSecondaryIndustries,
//     updatedTertiaryIndustries,
//   } = getUpdatedIndustries(
//     industryId,
//     depth,
//     primary,
//     secondary,
//     tertiary,
//     industryTree
//   );
//   return updateIndustryRule(
//     updatedPrimaryIndustries,
//     updatedSecondaryIndustries,
//     updatedTertiaryIndustries,
//     parsedRule
//   );
// };

export const getUpdatedFieldsRules = (
  rules: string,
  toggledMoreFilters: string[],
  toggledRule: Record<string, string>
) => {
  const parsedRules = parseRules(rules);
  const allFilterKeys = Object.entries(filters).map(
    ([_, filterKey]) => filterKey
  );
  const removedFields = parsedRules.filter((rule = []) => {
    if (!rule[0] || !rule[1]) return false;
    if (
      rule[0] === 'aor' &&
      allFilterKeys.includes(rule[1][0][0].toString()) === true
    )
      return false;
    return (
      allFilterKeys.includes(rule[0].toString()) === false ||
      rule[1] === 'is_not_null'
    );
  });
  return removedFields.concat(
    toggledMoreFilters.map((key) => parseRules(toggledRule[key]) as any)
  );
};
